// Shared react-select style objects for the reconciliation allocation row.
//
// The row hosts five selectors (Department / Cost Centre, Type, Budget Line,
// Transaction, Contract Reference). Their ~50-line style object used to be
// copy-pasted per control, so a visual change had to be made three to five
// times; the one variant they all use now lives here.
//
//   flat — borderless and transparent, so a page of 40 rows reads as table text
//          and only shows its controls on hover or focus.
//
// The flat control's BOX (height, border, background, placeholder colour and the
// hover-revealed indicator) is driven by `.reconciliation-select--flat` in
// sass/inputs/_custom-select.scss, not from here: the global
// `.react-select__control` / `.react-select__placeholder` rules in that file
// carry `!important`, which an emotion style object cannot out-weigh. What
// emotion still owns — inner heights and paddings, the portalled menu, and the
// per-option weights — stays in this file.

const FONT_SIZE = '12px';
const CHARCOAL = '#1B1C1D';
const CHARCOAL_50 = '#8D8E8E';

// react-select sizes a portalled menu from the CONTROL's rect (menuPortal base
// carries `width: rect.width`, the menu itself `width: 100%`). The Budget Line
// control sits in a tw-w-56 (~224px) column, shared with a group heading and its
// count badge, so a menu at cell width elides the parent segment of the path —
// the one place it must stay readable, since "Full Circle / Travel Costs" and
// "Beyond the Summit / Travel Costs" differ only there. The CLOSED control keeps
// its cell width; only the open menu is allowed to be wider.
//
// This is Budget-Line-specific (long names + parent path need the room), so it
// lives in `flatWide`, not `flat` — the other four selectors (Department, Type,
// Transaction, Contract Reference) also use `flat`, and a two-option Type menu
// has no business opening 22rem wide.
const MENU_MIN_WIDTH = '22rem';

/**
 * The parts every selector shares.
 *
 * The menu is portalled to <body> so it escapes the table's overflow container,
 * and a project / template / pack root (`is_parent`, `is_total`) is weighted so
 * it reads as a total rather than as one more line among its children.
 */
const shared = {
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menuList: (base) => ({
        ...base,
        maxHeight: '200px',
        overflowY: 'auto',
        fontSize: FONT_SIZE,
    }),
    option: (base, { data, isDisabled }) => ({
        ...base,
        fontSize: FONT_SIZE,
        fontWeight: data?.is_parent ? '600' : base.fontWeight,
        // A legacy child is listed so an existing allocation stays readable, but
        // it can never be picked again — muting it makes that read as deliberate
        // rather than as a rendering glitch.
        ...(isDisabled
            ? {
                  color: CHARCOAL_50,
                  fontStyle: 'italic',
                  cursor: 'not-allowed',
              }
            : {}),
    }),
};

export const flat = {
    ...shared,
    valueContainer: (base) => ({
        ...base,
        height: '22px',
        padding: '0 2px',
    }),
    input: (base) => ({
        ...base,
        margin: '0px',
        padding: '0px',
        fontSize: FONT_SIZE,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '22px',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 2px',
        transition: 'opacity 120ms ease-in-out',
    }),
    singleValue: (base, { data }) => ({
        ...base,
        margin: 0,
        maxWidth: '100%',
        fontWeight: data?.is_parent ? '700' : '500',
        color: CHARCOAL,
        fontSize: FONT_SIZE,
    }),
};

/**
 * `flat`, plus the widened portalled menu — Budget Line only. See MENU_MIN_WIDTH
 * above for why the other four selectors must not inherit this.
 */
export const flatWide = {
    ...flat,
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
        minWidth: MENU_MIN_WIDTH,
    }),
    menu: (base) => ({
        ...base,
        minWidth: MENU_MIN_WIDTH,
    }),
};

/** className for a flat selector — pairs the react-select prefix with the sass variant. */
export const FLAT_SELECT_CLASS = 'react-select-container reconciliation-select--flat';
